import { createContext, useContext, useEffect } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import * as authService from "@features/auth/services/auth-service";
import { setAuthToken } from "@core/api/api";

const AuthContext = createContext(null);

const TOKEN_KEY = "token";

export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient();

  // Get current user data
  const {
    data: user,
    isLoading: isLoadingUser,
    error: userError,
    refetch: refetchUser,
  } = useQuery({
    queryKey: ["user"],
    queryFn: authService.getCurrentUser,
    enabled: !!localStorage.getItem(TOKEN_KEY),
    retry: 1,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });

  // Set auth token on mount and token change
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    setAuthToken(token);
  }, []);

  // Login mutation
  const {
    mutate: login,
    isPending: isLoginPending,
    error: loginError,
  } = useMutation({
    mutationFn: authService.login,
    onSuccess: (data) => {
      localStorage.setItem(TOKEN_KEY, data.token);
      setAuthToken(data.token);
      queryClient.setQueryData(["user"], data.user);
    },
    onError: (error) => {
      localStorage.removeItem(TOKEN_KEY);
      setAuthToken(null);
      console.error("Login failed:", error);
    },
  });

  // Register mutation
  const {
    mutate: register,
    isPending: isRegisterPending,
    error: registerError,
  } = useMutation({
    mutationFn: authService.register,
    onSuccess: (data) => {
      // Optionally auto-login after registration
      if (data.token) {
        localStorage.setItem(TOKEN_KEY, data.token);
        setAuthToken(data.token);
        queryClient.setQueryData(["user"], data.user);
      }
    },
    onError: (error) => {
      console.error("Registration failed:", error);
    },
  });

  // Logout mutation
  const {
    mutate: logout,
    isPending: isLogoutPending,
    error: logoutError,
  } = useMutation({
    mutationFn: authService.logout,
    onSuccess: () => {
      localStorage.removeItem(TOKEN_KEY);
      setAuthToken(null);
      queryClient.clear(); // Clear all query cache
    },
    onError: (error) => {
      console.error("Logout failed:", error);
      // Force logout on client side even if server request fails
      localStorage.removeItem(TOKEN_KEY);
      setAuthToken(null);
      queryClient.clear();
    },
  });

  // Check if user has specific roles
  const hasRole = (roles) => {
    if (!user) return false;
    return Array.isArray(roles)
      ? roles.includes(user.role)
      : user.role === roles;
  };

  const value = {
    // User state
    user,
    isAuthenticated: !!user,
    isAdmin: hasRole("admin"),
    isManager: hasRole("manager"),
    isClientSupport: hasRole("clientSupport"),
    isClient: hasRole("client"),
    isDeliveryPerson: hasRole("livreur"),
    isCollector: hasRole("collector"),
    isAccountant: hasRole("accountant"),
    isManagerOrAdmin: hasRole(["manager", "admin", "clientSupport"]),
    isActive: user ? !user.inActive : false,

    // Loading states
    isLoading: isLoadingUser,
    isLoginPending,
    isRegisterPending,
    isLogoutPending,

    // Error states
    userError,
    loginError,
    registerError,
    logoutError,

    // Auth methods
    login,
    register,
    logout,
    refetchUser,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
